/**
 * ------ maintenance history ------
 *
 */

import { Component, OnInit, OnDestroy, ViewChild, Type } from '@angular/core';
import { Subscription, Subject, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { businessConstants } from '../../constants/business.constants';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { IntralinksMappingDialogParam } from './intralinks-mapping-dialog.view-model';
import { IntralinksMapping, IntralinksAccountInfo } from '../../tamalelibs/models/intralinks.model';
import { IntralinksMappingDialogService } from './intralinks-mapping-dialog.service';
import { EnumAaclTreeListNodeClickables, EnumAaclTreeListNodeClickHandlers, EnumAaclTreeListNodeHighlightMode, EnumAaclTreeListActionType } from 'aacl-tree-list';
import { IntralinksUpdateAction } from '../../tamalelibs/redux/intralinks.actions';
import { AppState } from '../../redux';
import { WidgetListItem, WidgetInstance, WidgetInstanceListFeedback } from './../widget-list/widget-list.model';
import { WidgetListComponent } from './../widget-list/widget-list.component';
import { catchError, filter, take } from 'rxjs/operators';
import { WebTemplateViewModel } from '../template/template-web.view-model';
import { ComponentTypeMapper } from '../template/component-type-mapper.service';
import { TemplateControlValue } from '../../tamalelibs/models/template-control-value.model';
import { DefaultEntryTemplateConfiguration } from '../../tamalelibs/constants/configuration.constants';
import { EntityService } from '../../tamalelibs/services/entity.service';
import { getPageStatusState } from '../../tamalelibs/redux/intralinks.reducer';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import * as _ from 'lodash';
import { UUID } from 'angular2-uuid';
import { TemplateControlConfig } from '../template/template-control-config.model';
import { NoteActions } from '../../tamalelibs/constants/business.constants';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';
import { ArrayHelperService } from '../../tamalelibs/services/array-helper.service';


@Component({
    selector: 'intralinks-mapping-dialog',
    templateUrl: './intralinks-mapping-dialog.component.html',
    styleUrls: ['./intralinks-mapping-dialog.component.scss'],
    providers: [WebTemplateViewModel]
})
export class IntralinksMappingDialogComponent implements OnInit, OnDestroy {
    @ViewChild(WidgetListComponent, { static: false }) widgetListComponent: WidgetListComponent;
    account: IntralinksAccountInfo;
    currentNode = null;
    defaultNoteType: any = null;
    entityConfig: TemplateControlConfig;
    entityReloading = false;
    folderId: string;
    form: IntralinksMapping;
    isOpen = false;
    isSaving = false;
    noteTypes: any[] = [];
    noteTypesSource: any[] = [];
    noteTypeFields: any[] = [];
    screenId: string;
    treeListOptions: any;
    waitingForInherit = false;
    widgetSubject: Subject<TemplateControlValue>;
    get isWorkspace(): boolean {
        return this.currentNode && this.currentNode.data && this.currentNode.data.folderType === 'workspace';
    }
    currentMapping: IntralinksMapping = null;
    private _instanceListSubscription: Subscription = null;
    private _requiredField: string[] = [];
    private _treeSubject$: Subject<any>;
    private _widgetList: WidgetListItem[] = [];
    private _destroySubscriptions: Array<Subscription> = [];


    constructor(
        private _alertWindow: AlertWindowService,
        private _dialogService: IntralinksMappingDialogService,
        private _store: Store<AppState>,
        private _noteTemplatePageViewModel: WebTemplateViewModel,
        private _componentTypeMapper: ComponentTypeMapper,
        private _entityService: EntityService,
        private _toast: ToastService,
    ) {
        this._treeSubject$ = new Subject();
    }

    ngOnInit() {

        this._destroySubscriptions.push(
            this._dialogService.open$.subscribe((param: IntralinksMappingDialogParam) => this._dialogOpenHandler(param))
        );

        this._destroySubscriptions.push(
            this._treeSubject$.subscribe(action => this._treeFeedbackHandler(action))
        );

        this._destroySubscriptions.push(
            this._noteTemplatePageViewModel.noteTypeForWeb().pipe(
                filter((res: any) => res != null && res.entryTypes.length > 0 && res.defaultEntryTypeId.length > 0),
            ).subscribe(res => this._onNoteTypesReady(res))
        );
        this._noteTemplatePageViewModel.getNoteTypes();

        this._destroySubscriptions.push(
            this._noteTemplatePageViewModel.noteTemplateConfiguration().pipe(
                filter(res => res && res.contains),
            ).subscribe(res => this._onNoteTypeFieldsReady(res.contains))
        );

        this.widgetSubject = new Subject<TemplateControlValue>();
        this._destroySubscriptions.push(
            this.widgetSubject.subscribe(this._onWidgetValueChanged.bind(this))
        );

        this.entityConfig = new TemplateControlConfig();
        this.entityConfig.isLabelHidden = true;
        this.entityConfig.width = 0;
        this.entityConfig.prefixId = '';

    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onCancelClick() {
        if (this._hasUnsavedChanges()) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._closeDialog();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._closeDialog();
        }
    }

    onSaveClick() {
        this._updateCurrentNode();
        const action = new IntralinksUpdateAction();
        action.payload = new IntralinksAccountInfo(
            this.account.username,
            this.account.password,
            this.account.alias,
            this.account.recipients,
            this.account.lastModifiedTime,
            this.account.lastModifiedUser);
        action.payload.endOtherSessions = !!this.account.endOtherSessions;
        action.payload.folderMappings = this.form;
        action.screenId = this.screenId;
        this._store.dispatch(action);
        this.isSaving = true;
        // this._closeDialog();
        this._store
            .select(getPageStatusState)
            .pipe(filter((state) => !!state), take(1))
            .subscribe(this._createdSuccessHandler.bind(this));

    }

    private _createdSuccessHandler(response) {
        let msg = '';
        const msgType = NotificationStyles.Success;
        switch (response.workflow) {
            case 'add':
                msg = businessConstants.notification.intralinks.createSuccessfullyMsg;
                this._closeDialog();
                this._toast.notify({
                    message: response.name + msg,
                    style: msgType
                });
                break;
            case 'edit':
                msg = businessConstants.notification.intralinks.editSuccessfullyMsg;
                this._closeDialog();
                this._toast.notify({
                    message: response.name + msg,
                    style: msgType
                });
                break;
            case 'add_fail':
            case 'edit_fail':
                let errorMsg = response.error.message;
                if (errorMsg.includes(businessConstants.errorMessage.ERR_INTRALINKS_SERVER)) {
                    errorMsg = StringLiteralsPipe.translate('general.the_configuration_is_not_correct');
                }
                this._toast.notify({
                    message: errorMsg,
                    style: NotificationStyles.Error
                });
                break;
        }
    }

    private _closeDialog() {
        this.isOpen = false;
        this.isSaving = false;
        this.account = null;
        this.form = null;
        this.treeListOptions = null;
        this.folderId = null;
        this.screenId = null;
        this.currentNode = null;
        this._requiredField = [];
        if (this._instanceListSubscription) {
            this._instanceListSubscription.unsubscribe();
            this._instanceListSubscription = null;
        }
        this.widgetListComponent.reset();
    }

    private _hasUnsavedChanges(): boolean {
        return false;
    }

    private _dialogOpenHandler(param: IntralinksMappingDialogParam) {

        const customizeClicks = new Map<EnumAaclTreeListNodeClickables, Array<EnumAaclTreeListNodeClickHandlers>>();
        customizeClicks.set(EnumAaclTreeListNodeClickables.ToggleIcon, [EnumAaclTreeListNodeClickHandlers.ExpandCollapse]);
        customizeClicks.set(EnumAaclTreeListNodeClickables.Text, [
            EnumAaclTreeListNodeClickHandlers.ExpandCollapse,
            EnumAaclTreeListNodeClickHandlers.Custom,
            EnumAaclTreeListNodeClickHandlers.Focus
        ]);

        this.treeListOptions = {
            items: param.treeItems,
            maxTreeLevel: 20,
            levelPadding: 10,
            customizeClicks: customizeClicks,
            collapseAllOnLoaded: true,
            actionSubject$: this._treeSubject$,
            feedbackSubject$: this._treeSubject$,
            enableTreeNodeFilter: false,
            nodeHighlightMode: EnumAaclTreeListNodeHighlightMode.Row,
            customClickHandler: this._treeNodeClickHandler.bind(this),
            // beforeExpandCollapseNode: (node) => this._splashMfaValidate(node.data)
            // filterSearchBoxPlaceholder: 'Search a field'
        };
        this.account = param.account;
        // param.account.folderMappings = null; //yoyo
        if (!param.account.folderMappings) {
            param.account.folderMappings = this._generateMappingFromTreeNode({ items: param.treeItems });
        }
        this.form = new IntralinksMapping(param.account.folderMappings);
        this.folderId = param.selectedFolderId;
        this.screenId = param.screenId;
        this.isOpen = true;
        this._expandNodesOnceTreeInited();

    }

    private _expandNodesOnceTreeInited() {
        if (this._treeSubject$.observers.length > 1) {
            this._treeSubject$.next({
                type: EnumAaclTreeListActionType.ExpandToNode,
                payload: (item) => {
                    if (item.data.folderId === this.folderId) {
                        return !item.hasChildren;
                    } else {
                        return item.parent && item.parent.data.folderId === this.folderId && item.index === 0;
                    }
                }
            });
            this._treeSubject$.next({
                type: EnumAaclTreeListActionType.ActivateNode,
                payload: (node) => {
                    const result = node.data.folderId === this.folderId;
                    if (result) {
                        this._treeNodeClickHandler(node);
                    }
                    return result;
                }
            });

            this._instanceListSubscription = this.widgetListComponent.instanceListSubject$.subscribe((feedback: WidgetInstanceListFeedback) => this._handleWidgetInstanceListFeedback(feedback));
        } else {
            setTimeout(() => {
                this._expandNodesOnceTreeInited();
            });
        }
    }

    private _treeFeedbackHandler(action) {
    }

    private _treeNodeClickHandler(node) {
        if (node.data && node.data.folderId) {
            this._onNodeSelected(node);
            if (!this.currentMapping.noteType.id) {
                this._onNoteTypeFieldsReady([]);
            }
            // this._initWigetList();
        }
    }

    private _initWigetList() {
        // generate instanceList
        const instanceList: WidgetInstance[] = [];
        if (this.currentMapping && this.currentMapping.fields) {
            // TAM-32023 nail required fields on wigdet list except [source,display date,priority, sentiment]
            const requiredWidgets = this._widgetList.filter(widget => widget.templateConfiguration.isRequired);
            requiredWidgets.forEach(widget => {
                if (!this.currentMapping.fields.find(field => widget.id === field.adhocDefId || widget.id === field.label) &&
                    this._componentTypeMapper.mapIsRequiredServer2UX(widget.templateConfiguration)) {
                    this._addFieldByTemplateConfig(widget.templateConfiguration);
                }
            });

            this.currentMapping.fields.forEach((field) => {
                const widgets = this._widgetList.filter((widget: WidgetListItem) => widget.id === field.adhocDefId || widget.id === field.label);
                if (widgets && widgets.length > 0) {
                    const undeletableTitle = this._componentTypeMapper.mapIsRequiredServer2UX(widgets[0].templateConfiguration) ? businessConstants.intralinkScreen.mappingDialog.undeletableTitle : null;
                    const instance = new WidgetInstance(
                        UUID.UUID(),
                        widgets[0].componentType,
                        (instanceWithRef: WidgetInstance) => {
                            widgets[0].componentInitializer(instanceWithRef.componentReference, instanceWithRef.value);
                        },
                        field.value,
                        undeletableTitle);
                    instance.selected = this.currentMapping.appliedFieldIds.includes(instance.id);
                    instance.widgetId = widgets[0].id;
                    instanceList.push(instance);
                }
            });
        }
        if (this.widgetListComponent) {
            // initialize widget-list Component
            this.widgetListComponent.init(this._widgetList, instanceList);
        }
    }

    private _onNodeSelected(node) {
        if (this.currentNode) {
            this._updateCurrentNode();
        }
        this.currentNode = node;
        const mapping = this.form.findByFolderId(node.data.folderId);
        if (mapping) {
            this.currentMapping = Object.assign(new IntralinksMapping(), { folderId: node.data.folderId, fields: [], appliedFieldIds: [] }, _.cloneDeep(mapping));
            // this.currentMapping.fields = mapping.fields.concat();
            if (!this.currentMapping.noteType || !this.currentMapping.noteType.id) {
                this.currentMapping.noteType = Object.assign({}, this.defaultNoteType);
            }
        } else {
            this.currentMapping = new IntralinksMapping({ folderId: node.data.folderId, fields: [], appliedFieldIds: [], noteType: this.defaultNoteType });
        }
        this._noteTemplatePageViewModel.getNoteTemplateConfiguration(this.currentMapping.noteType.id);
        this.folderId = node.data.folderId;
    }

    private _hasMappingChanged(mapping: IntralinksMapping) {
        if (!mapping || this.currentMapping.fields.length !== mapping.fields.length || mapping.noteType.id !== this.currentMapping.noteType.id || mapping.entities.length !== this.currentMapping.entities.length) {
            return true;
        } else {
            for (let i = 0; i < this.currentMapping.fields.length; i++) {
                if (this.currentMapping.fields[i].label !== mapping.fields[i].label || this.currentMapping.fields[i].adhocDefId !== mapping.fields[i].adhocDefId || this.currentMapping.fields[i].value !== mapping.fields[i].value) {
                    return true;
                }
            }

            for (let i = 0; i < this.currentMapping.entities.length; i++) {
                if (this.currentMapping.entities[i].id !== mapping.entities[i].id) {
                    return true;
                }
            }
        }
        return false;
    }

    //#region note type
    private _onNoteTypesReady(res: any) {
        if (res == null) {
            return;
        }
        if (res.entryTypes && res.entryTypes.length > 0) {
            this.noteTypesSource = res.entryTypes;
            ArrayHelperService.sort(this.noteTypesSource, 'name');
            this.noteTypes = this.noteTypesSource.slice();
            this.defaultNoteType = this.noteTypes.find(type => type.id === res.defaultEntryTypeId);
        }
    }

    public selectionChange(value: any): void {
        // this.currentMapping.noteType = value;
        this._noteTemplatePageViewModel.getNoteTemplateConfiguration(value.id);
    }

    private _onNoteTypeFieldsReady(properties: any[]) {
        // once note type fields are ready, generate widget list
        this.noteTypeFields = [];
        this._widgetList = [];
        for (const row of properties) {
            for (const cell of row) {
                if (!cell.isHidden) {
                    this._generateWidgetListItems(cell);
                }
            }
        }

        if (!this._widgetList || this._widgetList.length === 0) {
            const templateConfig = JSON.parse(DefaultEntryTemplateConfiguration);
            properties = templateConfig.contains;
            return this._onNoteTypeFieldsReady(properties);
        }

        if (this.waitingForInherit === true) {
            // inherit workflow
            this._inheritWidgets();
        } else {
            // this._calculateFieldsSelected();
            this._initWigetList();
        }

        this._requiredField = this.noteTypeFields.filter(field => this._componentTypeMapper.mapIsRequiredServer2UX(field) && field.controlID !== 'ENTITIES').map(field => field.adhocDefID && field.adhocDefID !== '' ? field.adhocDefID : (field.label === 'Date:' ? 'Display Date:' : field.label));
        const entityConfig = this.noteTypeFields.find(field => field.controlID === 'ENTITIES');
        if (entityConfig) {
            this._refreshEntities(entityConfig);
        }
        return this.noteTypeFields;
    }

    private _refreshEntities(entityConfig) {
        this.entityReloading = true;
        this.entityConfig = Object.assign(this.entityConfig, entityConfig, { isLabelHidden: true, width: 0, prefixId: '', defaultValue: null, noteAction: NoteActions.createNote });
        if (this.currentMapping) {
            if (Array.isArray(this.currentMapping.entities)) {
                this.entityConfig.staticDefaultValue = this.currentMapping.entities.map(entity => entity.id);
            }
        }
        setTimeout(() => {
            this.entityReloading = false;
        });
    }

    private _generateWidgetListItems(cell) {
        if (['virtualgroup', 'group'].includes(cell.componentType) && Array.isArray(cell.contains)) {
            for (let i = 0; i < cell.contains.length; i++) {
                if (Array.isArray(cell.contains[i])) {
                    for (let j = 0; j < cell.contains[i].length; j++) {
                        this._generateWidgetListItems(cell.contains[i][j]);
                    }
                }
            }
        } else {
            this._noteTypeField2WidgetListItem(cell);
        }
    }

    private _noteTypeField2WidgetListItem(cell) {
        const componentType = this._componentTypeMapper.getComponentType(cell.componentType);
        if (componentType) {
            if (cell.label === 'Date:' && !cell.adhocDefId) {
                cell.label = 'Display Date:';
            }
            const name = cell.label ? cell.label.split(':')[0].trim() : '';
            cell.label = name.length > 0 ? name + ':' : name;
            this.noteTypeFields.push(Object.assign({}, cell));
            if (name !== 'Entities') {
                this._widgetList.push(
                    new WidgetListItem(
                        cell.adhocDefId || cell.label,
                        name,
                        componentType,
                        1,
                        this._componentTypeMapper.getInitializer(cell.componentType, cell, this.widgetSubject),
                        cell
                    )
                );
            }
        }
    }

    public handleFilter(value, dropDownEl) {
        this.noteTypes = [];
        if (!value) {
            this.noteTypes = this.noteTypesSource;
        } else {
            this.noteTypes = this.noteTypesSource.filter((s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
            if (this.noteTypes.length >= 1) {
                setTimeout(() => {
                    dropDownEl.optionsList.content.nativeElement.scrollTop = 0;
                });
            }
        }
    }
    //#endregion

    private _onWidgetValueChanged(data: TemplateControlValue) {
        // if (data.isFromUser) {
        if (data.controlId === 'ENTITIES') {
            if (data.isFromUser) {
                this.currentMapping.entities = data.actualValue;
            }
        } else {
            this.currentMapping.fields.forEach(item => {
                if ((data.adhocDefId && item.adhocDefId === data.adhocDefId) || (data.label && item.label === data.label)) {
                    item.displayValue = data.displayValue;
                    if (data.value instanceof Date) {
                        item.value = data.value.getTime();
                    } else {
                        item.value = data.value;
                    }
                    item.actualValue = data.actualValue;
                }
            });
        }
        // }
    }

    private _updateCurrentNode() {
        let oldMapping = this.form.findByFolderId(this.folderId);
        if (oldMapping && this._hasMappingChanged(oldMapping)) {
            if (!this.currentNode.data.oldText) {
                this.currentNode.data.oldText = this.currentNode.data.dislpayText;
                this.currentNode.data.dislpayText = this.currentNode.data.dislpayText + '*';
            }
            this._calculateFieldsSelected();
            oldMapping = _.assign(oldMapping, this.currentMapping);
            oldMapping.requiredFields = [...this._requiredField];
            this.form = new IntralinksMapping(this.form); // reconstruct parent and child refrences
        } else {
            if (this.currentNode.data.oldText) {
                this.currentNode.data.dislpayText = this.currentNode.data.oldText;
                this.currentNode.data.oldText = null;
            }
            if (!oldMapping) {
                // tree structure
                this.form.children.push(this.currentMapping);
            }
        }
    }

    private _calculateFieldsSelected() {
        if (this.currentMapping) {
            if (this.widgetListComponent && this.widgetListComponent.instanceList && this.widgetListComponent.instanceList.length) {
                this.currentMapping.appliedFieldIds = this.widgetListComponent.instanceList.filter(instance => instance.selected).map(instance => instance.widgetId);
            } else {
                this.currentMapping.appliedFieldIds = [];
            }
        }
    }

    private _handleWidgetInstanceListFeedback(feedback: WidgetInstanceListFeedback) {
        switch (feedback.type) {
            case 'add':
                this._addFieldToCurrentMapping(feedback.payload.componentType, feedback.payload.widgetId);
                break;
            case 'delete':
                this.currentMapping.fields = this.currentMapping.fields.filter(field => field.adhocDefId !== feedback.payload.widgetId && field.label !== feedback.payload.widgetId);
                break;
            case 'refreshItem':
                const instance: WidgetInstance = feedback.payload;
                const index = this.widgetListComponent.instanceList.findIndex(item => item.id === instance.id);
                const widget = this._widgetList.find(item => item.id === instance.widgetId);
                const updatedValue = this._latestParentValue(instance.widgetId);
                if (index >= 0 && widget) {
                    const undeletableTitle = this._componentTypeMapper.mapIsRequiredServer2UX(widget.templateConfiguration) ? businessConstants.intralinkScreen.mappingDialog.undeletableTitle : null;
                    const updatedInstance = new WidgetInstance(
                        instance.id,
                        instance.componentType,
                        (instanceWithRef: WidgetInstance) => {
                            widget.componentInitializer(instanceWithRef.componentReference, instanceWithRef.value);
                        },
                        updatedValue,
                        undeletableTitle);
                    instance.selected = this.currentMapping.appliedFieldIds.includes(instance.id);
                    instance.widgetId = instance.widgetId;
                    this.widgetListComponent.instanceList[index] = updatedInstance;
                }
                break;
        }
    }

    private _addFieldToCurrentMapping(componentType: Type<any>, widgetId: string): boolean {
        const found = this._widgetList.find((item: WidgetListItem) => item.componentType === componentType && (item.id === widgetId));
        if (found) {
            this._addFieldByTemplateConfig(found.templateConfiguration);
            // this._addFieldsByTemplateConfig(found.templateConfiguration);
        }
        return !!found;
    }

    private _addFieldsByTemplateConfig(templateConfig: any) {
        if (Array.isArray(templateConfig.contains)) {
            for (let i = 0; i < templateConfig.contains.length; i++) {
                if (Array.isArray(templateConfig.contains[i])) {
                    for (let j = 0; j < templateConfig.contains[i].length; j++) {
                        this._addFieldByTemplateConfig(templateConfig.contains[i][j]);
                    }
                }
            }
        } else {
            this._addFieldByTemplateConfig(templateConfig);
        }
    }

    private _addFieldByTemplateConfig(templateConfig: any) {
        const field = new TemplateControlValue();
        field.componentType = templateConfig.componentType;
        field.label = templateConfig.label;
        field.adhocDefId = templateConfig.adhocDefID;
        field.controlId = templateConfig.controlID;
        this.currentMapping.fields.push(field);
    }

    private _generateMappingFromTreeNode(node: any, parent?: IntralinksMapping): IntralinksMapping {
        const result = new IntralinksMapping();
        result.folderId = node.folderId;
        result.isWorkspace = !!(node.folderId && node.folderId === node.workspaceId);
        if (parent) {
            result.parent = parent;
        }
        for (let i = 0; i < node.items.length; i++) {
            result.children.push(this._generateMappingFromTreeNode(node.items[i], result));
        }
        return result;
    }

    private _latestParentValue(id) {
        let mapping = this.currentMapping;
        while (mapping.parent) {
            const founds = mapping.parent.fields.filter(field => field.label === id || field.adhocDefId === id);
            if (founds && founds.length > 0) {
                return founds[0].value;
            }
            mapping = mapping.parent;
        }
        return null;
    }

    private _accumulateAncestorEntities() {
        const result = [];
        const ids = [];
        let mapping = this.currentMapping;
        while (mapping) {
            if (mapping.entities) {
                mapping.entities.forEach(entity => {
                    if (ids.indexOf(entity.id) < 0) {
                        ids.push(entity.id);
                        result.push(Object.assign({}, entity));
                    }
                });
            }
            mapping = mapping.parent;
        }
        return result;
    }

    inheritClicked() {
        this._alertWindow
            .warn(
                'Inherit Mapping From Parent',
                [
                    'Loading values from Parent folder will overwrite settings for the current folder.',
                    'Are you sure you want to apply Parent folder settings?'
                ],
                'Confirm',
                'Cancel'
            ).pipe(
                take(1)
            ).subscribe(confirmation => {
                if (confirmation) {
                    this._inheritEntities();
                    this._inheritNoteType();
                }
            });
    }

    private _inheritNoteType() {
        if (this.currentMapping.parent) {
            const noteType = this.currentMapping.parent.noteType;
            if (noteType.id && noteType.name) {
                this.waitingForInherit = true;
                this.currentMapping.noteType = _.cloneDeep(noteType);
                this.selectionChange(noteType);
            } else {
                this._refreshEntities({});
                this._inheritWidgets();
            }
        }
    }

    private _inheritEntities() {
        if (this.currentMapping.parent) {
            this.currentMapping.entities = _.cloneDeep(this.currentMapping.parent.entities);
        }
    }

    private _inheritWidgets() {
        if (this.currentMapping.parent && this.currentMapping.parent.fields) {
            this.currentMapping.fields = _.cloneDeep(this.currentMapping.parent.fields);
            this._initWigetList();
        }

        if (this.currentMapping.parent && this.currentMapping.parent.entities) {
            this.currentMapping.entities = [];
            this.currentMapping.parent.entities.forEach(entity => {
                this.currentMapping.entities.push(Object.assign({}, entity));
            });
            this._refreshEntities({});
        }
        this.waitingForInherit = false;
    }

    public inheritEntities() {
        this.currentMapping.entities = this._accumulateAncestorEntities();
        this._refreshEntities({});
    }
}
